import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Key, UserCheck, UserX, Shield, Eye, ArrowUpDown, X, AlertCircle } from 'lucide-react';

const UsersManager = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email_address: '',
    department: '',
    canView: '0',
    isAdmin: 'N'
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Counts state
  const [counts, setCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    admins: 0,
    canView: 0
  });

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      console.log('=== FETCHING USERS ===');
      const response = await fetch('/api/users');
      const data = await response.json();
      
      setUsers(data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
      setUsers([]);
    }
  };

  // Fetch counts
  const fetchCounts = async () => {
    try {
      const response = await fetch('/api/users/counts');
      const data = await response.json();
      setCounts(data);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  // Filter and sort users
  const filterAndSortUsers = () => {
    let filtered = [...users];

    // Apply status filter
    switch (activeFilter) {
      case 'active':
        filtered = filtered.filter(u => !u.inactive || u.inactive !== 'Y');
        break;
      case 'inactive':
        filtered = filtered.filter(u => u.inactive === 'Y');
        break;
      case 'admins':
        filtered = filtered.filter(u => u.isAdmin === 'Y' && (!u.inactive || u.inactive !== 'Y'));
        break;
      case 'canview':
        filtered = filtered.filter(u => u.CanView === '1' && (!u.inactive || u.inactive !== 'Y'));
        break;
      case 'all':
      default:
        // Show all users
        break;
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(u =>
        u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortField === 'created_date' || sortField === 'last_login') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
 const resetForm = () => {
  setFormData({
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email_address: '',
    department: '',
    canView: '0',
    isAdmin: 'N',
    user_role: 'basic'  // Add this line
  });
  setSelectedUser(null);
};

  // Reset password form
  const resetPasswordForm = () => {
    setPasswordForm({
      newPassword: '',
      confirmPassword: ''
    });
  };

  // Open modal
  const openModal = (mode, userData = null) => {
    setModalMode(mode);
    if (mode === 'edit' && userData) {
      setSelectedUser(userData);
      setFormData({
        username: userData.username || '',
        password: '', // Never pre-fill password
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email_address: userData.email_address || '',
        department: userData.department || '',
        canView: userData.CanView || '0',
        isAdmin: userData.isAdmin || 'N',
        user_role: userData.user_role || 'basic'  // Add this line
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  // Open password modal
  const openPasswordModal = (userData) => {
    setSelectedUser(userData);
    resetPasswordForm();
    setShowPasswordModal(true);
  };

  // Close modals
  const closeModals = () => {
    setShowModal(false);
    setShowPasswordModal(false);
    resetForm();
    resetPasswordForm();
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        createdBy: user.id,
        createdByName: user.fullName,
        updatedBy: user.id,
        updatedByName: user.fullName
      };

      const url = modalMode === 'add' ? '/api/users' : `/api/users/${selectedUser.id}`;
      const method = modalMode === 'add' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        closeModals();
        await Promise.all([fetchUsers(), fetchCounts()]);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Submit password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 4) {
      alert('Password must be at least 4 characters long');
      return;
    }

    try {
      const response = await fetch(`/api/users/${selectedUser.id}/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newPassword: passwordForm.newPassword,
          changedBy: user.id,
          changedByName: user.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        closeModals();
        alert('Password changed successfully');
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Password change error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Toggle user status
  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'Y';
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inactive: !newStatus,
          changedBy: user.id,
          changedByName: user.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        await Promise.all([fetchUsers(), fetchCounts()]);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Toggle status error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deletedBy: user.id,
          deletedByName: user.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        await Promise.all([fetchUsers(), fetchCounts()]);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error: ' + error.message);
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchCounts();
    fetchUsers();
  }, []);

  useEffect(() => {
    filterAndSortUsers();
  }, [users, searchTerm, sortField, sortDirection, activeFilter]);

  // Helper functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getUserStatusIcon = (userData) => {
    if (userData.inactive === 'Y') return <UserX className="w-4 h-4 text-red-500" />;
    if (userData.isAdmin === 'Y') return <Shield className="w-4 h-4 text-purple-500" />;
    if (userData.CanView === '1') return <Eye className="w-4 h-4 text-blue-500" />;
    return <UserCheck className="w-4 h-4 text-green-500" />;
  };

  const getUserStatusColor = (userData) => {
    if (userData.inactive === 'Y') return 'bg-red-100 text-red-800';
    if (userData.isAdmin === 'Y') return 'bg-purple-100 text-purple-800';
    if (userData.CanView === '1') return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  const getUserStatusText = (userData) => {
    if (userData.inactive === 'Y') return 'Inactive';
    if (userData.isAdmin === 'Y') return 'Admin';
    if (userData.CanView === '1') return 'PM User';
    return 'Basic User';
  };

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Status filters
  const filters = [
    { key: 'all', label: 'All Users', count: counts.total },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'inactive', label: 'Inactive', count: counts.inactive },
    { key: 'admins', label: 'Admins', count: counts.admins },
    { key: 'canview', label: 'PM Users', count: counts.canView }
  ];

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>System Users Manager</h1>
          <p style={{ color: '#6b7280' }}>Manage user accounts, permissions, and passwords</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => openModal('add')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
          >
            <Plus size={16} />
            Add User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {filters.map(filter => (
          <button
            key={filter.key}
            onClick={() => setActiveFilter(filter.key)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              background: activeFilter === filter.key ? '#2563eb' : 'white',
              color: activeFilter === filter.key ? 'white' : '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {filter.label}
            <span style={{
              background: activeFilter === filter.key ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
              padding: '0.125rem 0.375rem',
              borderRadius: '0.25rem',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              {filter.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={20} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search users by name, username, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      {/* Results count */}
      <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        Showing {currentUsers.length} of {filteredUsers.length} users
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.875rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('inactive')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Status <ArrowUpDown size={14} />
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('username')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Username <ArrowUpDown size={14} />
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('first_name')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Full Name <ArrowUpDown size={14} />
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Email & Department</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Permissions</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('created_date')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Created <ArrowUpDown size={14} />
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    Loading users...
                  </td>
                </tr>
              ) : currentUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No users found matching your criteria
                  </td>
                </tr>
              ) : (
                currentUsers.map(userData => (
                  <tr key={userData.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getUserStatusIcon(userData)}
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }} className={getUserStatusColor(userData)}>
                          {getUserStatusText(userData)}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{userData.username}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>ID: {userData.id}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>
                        {userData.first_name} {userData.last_name}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div>{userData.email_address || 'No email'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {userData.department || 'No department'}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {userData.isAdmin === 'Y' && (
                          <span style={{ 
                            background: '#fef3c7', color: '#92400e', 
                            padding: '0.125rem 0.375rem', borderRadius: '0.25rem', 
                            fontSize: '0.75rem', fontWeight: '500' 
                          }}>
                            Administrator
                          </span>
                        )}
                        {userData.CanView === '1' && (
                          <span style={{ 
                            background: '#dbeafe', color: '#1e40af', 
                            padding: '0.125rem 0.375rem', borderRadius: '0.25rem', 
                            fontSize: '0.75rem', fontWeight: '500' 
                          }}>
                            PM Access
                          </span>
                        )}
                        {userData.isAdmin !== 'Y' && userData.CanView !== '1' && (
                          <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>Basic User</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {userData.created_date ? new Date(userData.created_date).toLocaleDateString() : 'Unknown'}
                      </div>
                      {userData.last_login && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Last: {new Date(userData.last_login).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => openModal('edit', userData)}
                          style={{ padding: '0.25rem 0.5rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          <Edit size={12} />
                        </button>
                        
                        <button
                          onClick={() => openPasswordModal(userData)}
                          style={{ padding: '0.25rem 0.5rem', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          <Key size={12} />
                        </button>

                        <button
                          onClick={() => toggleUserStatus(userData.id, userData.inactive)}
                          style={{ 
                            padding: '0.25rem 0.5rem', 
                            background: userData.inactive === 'Y' ? '#16a34a' : '#dc2626', 
                            color: 'white', border: 'none', borderRadius: '0.25rem', 
                            fontSize: '0.75rem', cursor: 'pointer' 
                          }}
                        >
                          {userData.inactive === 'Y' ? <UserCheck size={12} /> : <UserX size={12} />}
                        </button>
                        
                        <button
                          onClick={() => deleteUser(userData.id)}
                          style={{ padding: '0.25rem 0.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '0.5rem', padding: '2rem', maxWidth: '600px', width: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                {modalMode === 'add' ? 'Add New User' : 'Edit User'}
              </h3>
              <button onClick={closeModals} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Username *</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                </div>
                {modalMode === 'add' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={modalMode === 'add'}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email Address</label>
                  <input
                    type="email"
                    name="email_address"
                    value={formData.email_address}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Department</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
  <div>
    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>User Role</label>
    <select
      name="user_role"
      value={formData.user_role}
      onChange={handleInputChange}
      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
    >
      <option value="basic">Basic User</option>
      <option value="asset_manager">Asset Manager (3 Pages Only)</option>
      <option value="pm_user">PM User (Full PM Access)</option>
      <option value="admin">Administrator</option>
      <option value="cert_tracker">Cert Tracker</option>
      <option value="qms_user">QMS User (QMS Assistant Only)</option>
    </select>
  </div>
  <div>
    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>PM Access (Legacy)</label>
    <select
      name="canView"
      value={formData.canView}
      onChange={handleInputChange}
      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
    >
      <option value="0">No PM Access</option>
      <option value="1">PM Access Granted</option>
    </select>
  </div>
  <div>
    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Administrator (Legacy)</label>
    <select
      name="isAdmin"
      value={formData.isAdmin}
      onChange={handleInputChange}
      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
    >
      <option value="N">Regular User</option>
      <option value="Y">Administrator</option>
    </select>
  </div>
</div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={closeModals}
                  style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  {modalMode === 'add' ? 'Add User' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '0.5rem', padding: '2rem', maxWidth: '400px', width: '90vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                Change Password
              </h3>
              <button onClick={closeModals} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0f9ff', borderRadius: '0.375rem' }}>
              <div style={{ fontWeight: '500' }}>User: {selectedUser?.username}</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {selectedUser?.first_name} {selectedUser?.last_name}
              </div>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>New Password *</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="4"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="4"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={closeModals}
                  style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '0.5rem 1rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManager;